import { useContext, useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, FormControlLabel, Switch, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { isDocEditable, sectionSelectors, selectDocument } from '../documentSlice';
import { impossible } from '../../../app/Utils';
import { updateDocSection } from '../documentsApi';
import { APIError } from '../../../app/appTypes';
import { Localized } from '@fluent/react';
import { showError } from '../../notifications/notificationsSlice';
import { SectionDeleteContext } from '../DocumentContent';
import { Attachements } from '../../urs/Attachements';
import { BlobResourceType } from '../blobResourceTypes';
import { DocumentSectionDeleteDialog } from '../utils/DeleteSectionDialog';
import { PrintPreviewDocumentAttachments } from './PrintPreviewDocumentAttachments';
import { DocumentSectionHeader } from '../components/DocumentSectionHeader';
import { DocumentAttachmentChild, loadBlobs, loadDocumentAttachmentChildren, remarksRequiredForDocumentStatus, selectAttachements } from '../attachementsSlice';

// Reserved tree-item ids. Leaf items use the child resourceId directly; group headers are inert
// (selecting them only expands/collapses, never changes the right pane).
const ROOT_ITEM = 'root'
const REQUIREMENTS_GROUP = 'group-requirements'
const REFERENCES_GROUP = 'group-references'

export const DocumentAttachmentsWrapper = (props: { sectionId: string }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const deleteSection = useContext(SectionDeleteContext)
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()

    const { numeration } = section

    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const isEditable = useAppSelector(isDocEditable)
    const [isPreview, setIsPreview] = useState(false);
    // Once the document leaves NEW, every attachment change has to be justified — children included,
    // since the API judges them by their owning document.
    const requireRemarks = remarksRequiredForDocumentStatus(header.status)

    // Left-tree state: the document's child objects (requirements/references) that have attachments.
    const [children, setChildren] = useState<DocumentAttachmentChild[]>([])
    const [childrenLoading, setChildrenLoading] = useState(false)
    const [selectedItem, setSelectedItem] = useState<string>(ROOT_ITEM)

    const attachementsState = useAppSelector(selectAttachements)

    useEffect(() => {
        let active = true
        const load = async () => {
            setChildrenLoading(true)
            try {
                const result = await dispatch(loadDocumentAttachmentChildren(header.guid)).unwrap()
                if (active) setChildren(result)
            } catch {
                if (active) dispatch(showError('error'))
            } finally {
                if (active) setChildrenLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [dispatch, header.guid])

    // Once a node's blobs are loaded in the store, prefer that live count over the backend snapshot —
    // so deleting the last file of a child drops its leaf, and uploads bump the badge, reactively.
    const liveCount = (resourceId: string): number | undefined => {
        const entry = attachementsState.entities[resourceId]
        return entry?.blobs == null ? undefined : entry.blobs.length
    }
    const displayCount = (child: DocumentAttachmentChild): number => liveCount(child.resourceId) ?? child.blobCount

    const rootCount = liveCount(header.guid)
    const visibleChildren = children.filter(c => displayCount(c) > 0)
    const requirements = visibleChildren.filter(c => c.resourceType === BlobResourceType.UrsRequirement)
    const references = visibleChildren.filter(c => c.resourceType === BlobResourceType.DocumentReference)

    const selectedChild = visibleChildren.find(c => c.resourceId === selectedItem)
    const isRoot = !selectedChild
    const selectedResourceId = selectedChild?.resourceId ?? header.guid

    // Ensure the selected node's blobs are in the store for the right pane (and for liveCount).
    useEffect(() => {
        dispatch(loadBlobs(selectedResourceId))
    }, [dispatch, selectedResourceId])

    // A selected child whose files were all deleted is no longer visible → fall back to the root node.
    useEffect(() => {
        if (selectedItem !== ROOT_ITEM && !selectedChild && !childrenLoading) {
            setSelectedItem(ROOT_ITEM)
        }
    }, [selectedItem, selectedChild, childrenLoading])

    const handleApprove = async (newName: string) => {
        if (newName) {
            try {
                await dispatch(updateDocSection({ ...section, name: newName })).unwrap()
            } catch (error) {
                setError(error as APIError)
            }
        }
    };

    const handleDeleteClick = () => setDel(true)

    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }
    const handleCancelDelClick = () => setDel(false)

    const handleSelect = (_: React.SyntheticEvent, itemId: string | null) => {
        // Group headers are not selectable destinations; only the root and leaf children drive the pane.
        if (itemId && itemId !== REQUIREMENTS_GROUP && itemId !== REFERENCES_GROUP) {
            setSelectedItem(itemId)
        }
    }

    const leafLabel = (child: DocumentAttachmentChild) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
            <Typography variant="body2" noWrap title={child.label}>{child.label}</Typography>
            <Chip size="small" label={displayCount(child)} />
        </Box>
    )

    return (
        <>
            <DocumentSectionHeader
                numeration={numeration}
                name={section.name}
                isEditable={isEditable}
                error={error}
                onSaveName={handleApprove}
                onDeleteClick={handleDeleteClick}
            />

            <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", mt: 1 }}>
                <Box sx={{ width: 280, flexShrink: 0, borderRight: 1, borderColor: "divider", pr: 1 }}>
                    {childrenLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <SimpleTreeView
                            slots={{ collapseIcon: ArrowDropDownIcon, expandIcon: ArrowRightIcon }}
                            selectedItems={selectedItem}
                            multiSelect={false}
                            onSelectedItemsChange={handleSelect}
                            defaultExpandedItems={[REQUIREMENTS_GROUP, REFERENCES_GROUP]}
                        >
                            <TreeItem
                                itemId={ROOT_ITEM}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
                                        <Localized id="document-attachments-root"><span>Document</span></Localized>
                                        {rootCount !== undefined && <Chip size="small" label={rootCount} />}
                                    </Box>
                                }
                            />
                            {requirements.length > 0 &&
                                <TreeItem
                                    itemId={REQUIREMENTS_GROUP}
                                    label={<Localized id="document-attachments-requirements"><span>Requirements</span></Localized>}
                                >
                                    {requirements.map(c => <TreeItem key={c.resourceId} itemId={c.resourceId} label={leafLabel(c)} />)}
                                </TreeItem>}
                            {references.length > 0 &&
                                <TreeItem
                                    itemId={REFERENCES_GROUP}
                                    label={<Localized id="document-attachments-references"><span>References</span></Localized>}
                                >
                                    {references.map(c => <TreeItem key={c.resourceId} itemId={c.resourceId} label={leafLabel(c)} />)}
                                </TreeItem>}
                        </SimpleTreeView>
                    )}
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                        <FormControlLabel
                            control={<Switch checked={isPreview} size="small" onChange={() => setIsPreview(v => !v)} />}
                            label={<Localized id="print-preview">
                                <span>_Print preview</span>
                            </Localized>}
                        />
                    </Box>
                    {isPreview ? (
                        <PrintPreviewDocumentAttachments resourceId={selectedResourceId} lang={header.language} />
                    ) : isRoot ? (
                        <Attachements resourceId={header.guid} resourceType={BlobResourceType.Document} canDelete={isEditable} canUpload={isEditable} requireRemarks={requireRemarks} />
                    ) : (
                        <Attachements
                            resourceId={selectedChild!.resourceId}
                            resourceType={selectedChild!.resourceType}
                            parentResourceId={header.guid}
                            parentResourceType={BlobResourceType.Document}
                            canDelete={isEditable}
                            canUpload={isEditable}
                            requireRemarks={requireRemarks}
                        />
                    )}
                </Box>
            </Box>

            {del &&
                <DocumentSectionDeleteDialog
                    deleting={deleting}
                    hasSubSections={section.subSectionsIds.length > 0}
                    onConfirm={handleConfirmDeleteClick}
                    onCancel={handleCancelDelClick}
                />
            }

        </>
    );
}
